'use client';

import { useState, useEffect } from 'react';
import HeroSection from './HeroSection';
import QuickAccessCards from './QuickAccessCards';
import FAQSection from './FAQSection';
import VideoTutorials from './VideoTutorials';
import ContactChannels from './ContactChannels';
import KnowledgeBase from './KnowledgeBase';
import LiveChatWidget from './LiveChatWidget';
import SupportTicketForm from './SupportTicketForm';
import Icon from '@/components/ui/AppIcon';
import { sendSupportTicket } from '@/utils/emailService';

interface TicketData {
  name: string;
  email: string;
  phone: string;
  category: string;
  priority: string;
  subject: string;
  description: string;
}

export default function SupportCenterInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isTicketFormOpen, setIsTicketFormOpen] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error';message: string;} | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const quickAccessCards = [
  {
    id: 1,
    icon: 'ChatBubbleLeftRightIcon',
    title: 'Live Chat',
    description: 'Razgovarajte s našim agentima u realnom vremenu',
    action: 'chat'
  },
  {
    id: 2,
    icon: 'PhoneIcon',
    title: 'Pozovite nas',
    description: 'Dostupni smo radnim danima 8:00-17:00',
    action: 'phone'
  },
  {
    id: 3,
    icon: 'EnvelopeIcon',
    title: 'Email podrška',
    description: 'Pošaljite nam email i odgovorićemo u roku od 24h',
    action: 'email'
  },
  {
    id: 4,
    icon: 'TicketIcon',
    title: 'Kreiraj tiket',
    description: 'Otvorite tiket za praćenje vašeg zahtjeva',
    action: 'ticket'
  }];


  const faqs = [
  {
    id: 1,
    category: 'Internet',
    question: 'Kako mogu povećati brzinu interneta?',
    answer: 'Nadogradnja brzine je moguća u bilo kojem trenutku. Kontaktirajte nas putem chata ili pozovite 0800 20502 da provjerimo dostupnost brzih paketa na vašoj adresi.'
  },
  {
    id: 2,
    category: 'Internet',
    question: 'Šta je potrebno za instalaciju interneta?',
    answer: 'Za instalaciju je potrebna važeća lična karta i pristup lokaciji gdje će se instalirati oprema. Naš tehničar će doći u dogovorenom terminu.'
  },
  {
    id: 3,
    category: 'Naplata',
    question: 'Kako mogu platiti račun?',
    answer: 'Račun možete platiti putem mobilne aplikacije, internet bankarstvom, u pošti, bankama ili na prodajnim mjestima Telesky.'
  },
  {
    id: 4,
    category: 'Naplata',
    question: 'Kada stiže račun?',
    answer: 'Račun vam pristiže ukoliko ste podnijeli zahtjev za dostavu istog na način koji ste odabrali prilikom uvođenja usluge i to na datum minimalno 10 dana prije isteka mjesečnog ciklusa. Telekom operateri nisu dužni izdavati pojedinačno fiskalne račune ali ukoliko želite isti možete zatražit u najbližoj poslovnici Telesky.'
  },
  {
    id: 5,
    category: 'Tehnička podrška',
    question: 'Internet ne radi, šta da radim?',
    answer: 'Prvo provjerite da li su svi kablovi pravilno spojeni i restartujte router. Ako problem i dalje postoji, kontaktirajte našu tehničku podršku na 0800 20502 ili putem live chat forme.'
  },
  {
    id: 6,
    category: 'Tehnička podrška',
    question: 'Kako resetovati lozinku za Wi-Fi?',
    answer: 'Lozinku možete promijeniti putem admin panela routera ili kontaktiranjem naše podrške koja će vam pomoći u procesu.'
  },
  {
    id: 7,
    category: 'Ugovor',
    question: 'Kako mogu raskinuti ugovor?',
    answer: 'Raskid ugovora možete zatražiti putem pisanog zahtjeva na našim prodajnim mjestima ili slanjem skeniranog zahtjeva na email.'
  },
  {
    id: 8,
    category: 'Ugovor',
    question: 'Koliko traje ugovorni period?',
    answer: 'Standardni ugovorni period je 24 mjeseca. Nakon isteka, ugovor se automatski produžava na neodređeno vrijeme.'
  }];


  const videos = [
  {
    id: 1,
    title: 'Podešavanje Wi-Fi routera',
    description: 'Naučite kako pravilno podesiti i optimizovati vaš Wi-Fi router',
    duration: '5:20',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7aaaeb8-1764652219159.png",
    thumbnailAlt: 'Modern white wireless router with blue LED lights on wooden desk in home office setting',
    category: 'Internet'
  },
  {
    id: 2,
    title: 'Provjera potrošnje interneta',
    description: 'Kako pratiti vašu potrošnju internet podataka',
    duration: '2:30',
    thumbnail: "https://images.unsplash.com/photo-1647964186028-73747bdbb4a0",
    thumbnailAlt: 'Hand holding smartphone displaying data usage statistics with colorful graphs and charts',
    category: 'Internet'
  },
  {
    id: 3,
    title: 'Online plaćanje računa',
    description: 'Jednostavan vodič za plaćanje računa putem web portala',
    duration: '4:15',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_104b51d4e-1764676502505.png",
    thumbnailAlt: 'Laptop screen showing online payment interface with credit card and secure payment icons',
    category: 'Naplata'
  },
  {
    id: 4,
    title: 'Promjena internet paketa',
    description: 'Kako promijeniti vaš internet paket u nekoliko klikova',
    duration: '3:00',
    thumbnail: "https://images.unsplash.com/photo-1618815909276-c8bfe7baba26",
    thumbnailAlt: 'Mobile phone displaying package selection screen with different data plan options',
    category: 'Internet'
  },
  {
    id: 5,
    title: 'Rješavanje problema s mrežom',
    description: 'Osnovni koraci za rješavanje najčešćih problema s mrežom',
    duration: '6:10',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_17c7468b1-1764686359127.png",
    thumbnailAlt: 'Network diagnostic screen showing signal strength bars and troubleshooting options',
    category: 'Tehnička podrška'
  }];


  const contactChannels = [
  {
    id: 1,
    icon: 'PhoneIcon',
    title: 'Telefonska podrška',
    description: 'Pozovite nas za brzu pomoć',
    availability: 'Pon-Pet: 8:00-17:00, Sub: 9:00-17:00',
    action: 'phone',
    actionLabel: 'Pozovi 035 560 560'
  },
  {
    id: 2,
    icon: 'EnvelopeIcon',
    title: 'Email podrška',
    description: 'Pošaljite nam detaljan upit',
    availability: 'Odgovor u roku od 24h',
    action: 'email',
    actionLabel: 'Pošalji email'
  },
  {
    id: 3,
    icon: 'MapPinIcon',
    title: 'Prodajna mjesta',
    description: 'Posjetite nas lično',
    availability: 'Pon-Pet: 8:00-17:00',
    action: 'location',
    actionLabel: 'Pronađi lokaciju'
  }];


  const knowledgeCategories = [
  {
    id: 1,
    icon: 'WifiIcon',
    title: 'Internet usluge',
    description: 'Vodiči za internet pakete, instalaciju i podešavanje',
    articleCount: 18
  },
  {
    id: 2,
    icon: 'TvIcon',
    title: 'TV paketi',
    description: 'Sve o TV paketima i dodatnim opcijama',
    articleCount: 15
  },
  {
    id: 3,
    icon: 'CreditCardIcon',
    title: 'Naplata i plaćanje',
    description: 'Informacije o računima, plaćanju i načinima naplate',
    articleCount: 15
  },
  {
    id: 4,
    icon: 'WrenchScrewdriverIcon',
    title: 'Tehnička podrška',
    description: 'Rješavanje tehničkih problema i optimizacija',
    articleCount: 32
  },
  {
    id: 5,
    icon: 'DocumentTextIcon',
    title: 'Ugovori i politike',
    description: 'Uslovi korištenja, politike privatnosti i ugovori',
    articleCount: 12
  },
  {
    id: 6,
    icon: 'ShieldCheckIcon',
    title: 'Sigurnost',
    description: 'Zaštita podataka, sigurnost mreže i privatnost',
    articleCount: 9
  }];


  if (!isHydrated) {
    return (
      <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:244:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="244" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:245:8" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="245" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%22%7D" className="h-16" />
        <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:246:8" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="246" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:247:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="247" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%22%7D" className="h-96 bg-muted" />
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:248:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="248" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20py-16%20space-y-8%22%7D" className="max-w-7xl mx-auto px-4 py-16 space-y-8">
            <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:249:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="249" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-xl%22%7D" className="h-64 bg-muted rounded-xl" />
            <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:250:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="250" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" className="h-96 bg-muted rounded-xl" />
          </div>
        </div>
      </div>);

  }

  const handleQuickAccessClick = (action: string) => {
    switch (action) {
      case 'chat':
        setIsChatOpen(true);
        break;
      case 'phone':
        showNotification('success', 'Pozovite nas na broj: 035 560 560');
        break;
      case 'email':
        showNotification('success', 'Pošaljite email na: kontakt@telesky.ba');
        break;
      case 'ticket':
        setIsTicketFormOpen(true);
        break;
    }
  };

  const handleChannelAction = (action: string) => {
    switch (action) {
      case 'phone':
        showNotification('success', 'Pozovite nas na broj: 035 560 560');
        break;
      case 'email':
        showNotification('success', 'Pošaljite email na: kontakt@telesky.ba');
        break;
      case 'location':
        showNotification('success', 'Pronađite najbližu lokaciju na mapi pokrivenosti');
        break;
    }
  };

  const handleVideoClick = (videoId: number) => {
    showNotification('success', 'Video tutorial će se uskoro otvoriti');
  };

  const handleCategoryClick = (categoryId: number) => {
    showNotification('success', 'Otvaranje baze znanja...');
  };

  const handleTicketSubmit = async (data: TicketData) => {
    setIsTicketFormOpen(false);
    
    // Show loading notification
    showNotification('success', 'Slanje tiketa u toku...');
    
    try {
      const result = await sendSupportTicket(data);
      
      if (result.success) {
        showNotification('success', `Tiket uspješno poslat! Broj tiketa: #${Math.floor(Math.random() * 10000)}`);
      } else {
        showNotification('error', result.error || 'Greška pri slanju tiketa. Molimo pokušajte ponovo.');
      }
    } catch (error) {
      showNotification('error', 'Greška pri slanju tiketa. Molimo pokušajte ponovo.');
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  return (
    <>
      <HeroSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:322:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="322" data-component-file="SupportCenterInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D"
        onSearchClick={() => setShowSearchBar(true)}
        onChatClick={() => setIsChatOpen(true)} />


      {showSearchBar &&
      <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:328:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="328" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22sticky%20top-16%20z-40%20bg-card%20border-b%20border-border%20shadow-minimal%22%7D" className="sticky top-16 z-40 bg-card border-b border-border shadow-minimal">
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:329:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="329" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-5xl%20mx-auto%20px-4%20py-4%22%7D" className="max-w-5xl mx-auto px-4 py-4">
            <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:330:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="330" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
              <Icon data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:331:14" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="331" data-component-file="SupportCenterInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-4%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
              name="MagnifyingGlassIcon"
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />

              <input data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:336:14" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="336" data-component-file="SupportCenterInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20pl-12%20pr-12%20py-4%20border%20border-border%20rounded-xl%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%20bg-background%20text-foreground%22%7D"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pretražite FAQ, vodiče i članke..."
              className="w-full pl-12 pr-12 py-4 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              autoFocus />

              {searchQuery &&
            <button data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:345:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="345" data-component-file="SupportCenterInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-4%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%20hover%3Atext-foreground%22%7D"
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">

                  <Icon data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:349:18" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="349" data-component-file="SupportCenterInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={20} />
                </button>
            }
            </div>
          </div>
        </div>
      }

      <QuickAccessCards data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:357:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="357" data-component-file="SupportCenterInteractive.tsx" data-component-name="QuickAccessCards" data-component-content="%7B%22elementName%22%3A%22QuickAccessCards%22%7D" cards={quickAccessCards} onCardClick={handleQuickAccessClick} />
      <FAQSection data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:358:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="358" data-component-file="SupportCenterInteractive.tsx" data-component-name="FAQSection" data-component-content="%7B%22elementName%22%3A%22FAQSection%22%7D" faqs={faqs} searchQuery={searchQuery} />
      <VideoTutorials data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:359:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="359" data-component-file="SupportCenterInteractive.tsx" data-component-name="VideoTutorials" data-component-content="%7B%22elementName%22%3A%22VideoTutorials%22%7D" videos={videos} onVideoClick={handleVideoClick} />
      <ContactChannels data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:360:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="360" data-component-file="SupportCenterInteractive.tsx" data-component-name="ContactChannels" data-component-content="%7B%22elementName%22%3A%22ContactChannels%22%7D" channels={contactChannels} onChannelAction={handleChannelAction} />
      <KnowledgeBase data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:361:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="361" data-component-file="SupportCenterInteractive.tsx" data-component-name="KnowledgeBase" data-component-content="%7B%22elementName%22%3A%22KnowledgeBase%22%7D" categories={knowledgeCategories} onCategoryClick={handleCategoryClick} />

      <LiveChatWidget data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:363:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="363" data-component-file="SupportCenterInteractive.tsx" data-component-name="LiveChatWidget" data-component-content="%7B%22elementName%22%3A%22LiveChatWidget%22%7D" isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      <SupportTicketForm data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:364:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="364" data-component-file="SupportCenterInteractive.tsx" data-component-name="SupportTicketForm" data-component-content="%7B%22elementName%22%3A%22SupportTicketForm%22%7D"
        isOpen={isTicketFormOpen}
        onClose={() => setIsTicketFormOpen(false)}
        onSubmit={handleTicketSubmit} />


      {notification &&
      <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:371:6" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="371" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-6%20left-6%20z-50%20max-w-md%20animate-elevation-rise%22%7D" className="fixed bottom-6 left-6 z-50 max-w-md animate-elevation-rise">
          <div data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:372:10" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="372" data-component-file="SupportCenterInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex items-start space-x-3 p-4 rounded-xl shadow-atmospheric ${
        notification.type === 'success' ? 'bg-success text-white' : 'bg-error text-white'}`
        }>
            <Icon data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:375:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="375" data-component-file="SupportCenterInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22flex-shrink-0%22%7D"
            name={notification.type === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'}
            size={24}
            className="flex-shrink-0" />

            <p data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:380:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="380" data-component-file="SupportCenterInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-medium%22%7D" className="text-sm font-medium">{notification.message}</p>
            <button data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:381:12" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="381" data-component-file="SupportCenterInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22ml-auto%20hover%3Aopacity-80%20transition-smooth%22%7D"
            onClick={() => setNotification(null)}
            className="ml-auto hover:opacity-80 transition-smooth">

              <Icon data-component-id="src/app/support-center/components/SupportCenterInteractive.tsx:385:14" data-component-path="src/app/support-center/components/SupportCenterInteractive.tsx" data-component-line="385" data-component-file="SupportCenterInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={20} />
            </button>
          </div>
        </div>
      }
    </>);

}